import type { SpecialQualifyingStatus } from '@iheartjane/data-access';
import type {
  SmartSortProduct,
  VirtualBudtenderProduct,
} from '@iheartjane/dm-sdk';
import { EXTRACT, PRICE_ID_TO_GRAMS } from '@iheartjane/menu';
import type { PriceId, ProductWeight, SpecialType } from '@iheartjane/types';

import type { AlgoliaProduct } from '@jane/search/types';
// eslint-disable-next-line no-restricted-imports
import type { PendingCartProduct } from '@jane/shared/models';
import type {
  MenuProduct,
  ProductThresholdConditions,
  StoreSpecial,
} from '@jane/shared/types';
import { isSoldByWeight, maybeParseFloat } from '@jane/shared/util';

// get the MMP attribute needed to query eligible qualifier/discountable products
export const getSpecialFilterAttribute = (
  specialType?: SpecialType,
  qualifyingStatus?: string | null
) => {
  switch (specialType) {
    case 'cart_total': {
      return 'applicable_cart_total_special_ids';
    }
    case 'bulk_pricing': {
      return 'applicable_bulk_special_ids';
    }
    case 'bundle': {
      return qualifyingStatus === 'applied' || qualifyingStatus === 'qualified'
        ? 'applicable_bundle_special_ids.discount'
        : 'applicable_bundle_special_ids.qualify';
    }
    case 'spending_threshold': {
      return qualifyingStatus === 'applied' || qualifyingStatus === 'qualified'
        ? 'applicable_any_discountable_special_ids'
        : 'applicable_any_qualifier_special_ids';
    }
    default: {
      return 'applicable_special_ids';
    }
  }
};

const productInCart = (
  cartProducts: PendingCartProduct[],
  productId: number,
  priceId?: PriceId
) => {
  return cartProducts?.some(
    (cartProduct) =>
      cartProduct.product_id === productId &&
      (!priceId || cartProduct.price_id === priceId)
  );
};

interface ShouldShowWeight {
  cartProducts?: PendingCartProduct[];
  currentSpecial?: StoreSpecial;
  enableNonStandardWeights?: boolean;
  qualifyingStatus?: SpecialQualifyingStatus | null;
  showOnlyWeights?: string[];
}

/**
 * Creates a weight filtering function based on special conditions.
 * Returns a function that determines whether a specific weight should be shown,
 * handling cases like cart status, qualifying status, bundle conditions, and price thresholds.
 * The returned function maintains closure over the provided special conditions, cart state,
 * and qualifying status, allowing efficient reuse across multiple weight checks.
 *
 * Handles different special types:
 * - Bulk pricing: Checks weight limits, price thresholds, and remaining target weight
 * - Bundle: Uses dependent/independent weights based on qualification status
 * - Product/Cart Total: Filters based on allowed weights and price thresholds
 * - Spending Threshold: Filters based on allowed weights and price thresholds for qualifiers or discountables depending on qualification status
 */
export const shouldShowWeight = ({
  cartProducts = [],
  currentSpecial,
  qualifyingStatus,
  showOnlyWeights,
  enableNonStandardWeights,
}: ShouldShowWeight) => {
  return (weight: string, product: MenuProduct | AlgoliaProduct) => {
    if (!currentSpecial) return true;

    const productId = 'id' in product ? product.id : product.product_id;
    const weightId = weight.replace(' ', '_') as ProductWeight;

    const stringPrice = product[`price_${weightId}`];
    const stringDiscountedPrice = product[`discounted_price_${weightId}`];
    const price = stringPrice && maybeParseFloat(stringPrice);
    const discountedPrice =
      stringDiscountedPrice && maybeParseFloat(stringDiscountedPrice);

    if (productInCart(cartProducts, productId, weightId)) return true;

    if (showOnlyWeights?.length && !showOnlyWeights.includes(weightId)) {
      return false;
    }

    if ((stringDiscountedPrice && discountedPrice === 0) || !price)
      return false;

    if (currentSpecial?.special_type === 'spending_threshold') {
      const isQualifiedBundle = ['qualified', 'applied'].includes(
        qualifyingStatus?.qualifying_status || ''
      );
      const rules = isQualifiedBundle
        ? currentSpecial.rules?.discountable
        : currentSpecial.rules?.qualifier;
      const productThreshold = rules?.product_threshold;
      const includesWeights = rules?.includes?.[0]?.weights || [];
      const excludesWeights = rules?.excludes?.[0]?.weights || [];

      return rulesIncludeWeight(
        includesWeights,
        excludesWeights,
        !!enableNonStandardWeights,
        productThreshold,
        weightId,
        price
      );
    }

    if (currentSpecial?.special_type === 'product') {
      const includesWeights =
        currentSpecial.rules?.includes?.[0]?.weights || [];
      const excludesWeights =
        currentSpecial.rules?.excludes?.[0]?.weights || [];
      const productThreshold = currentSpecial.rules?.product_threshold;

      return rulesIncludeWeight(
        includesWeights,
        excludesWeights,
        !!enableNonStandardWeights,
        productThreshold,
        weightId,
        price
      );
    }

    if (currentSpecial?.special_type === 'bundle') {
      const isQualifiedBundle = ['qualified', 'applied'].includes(
        qualifyingStatus?.qualifying_status || ''
      );
      const specialCondition = isQualifiedBundle
        ? currentSpecial.conditions?.bundle?.dependent
        : currentSpecial.conditions?.bundle?.independent;

      if (specialCondition) {
        const { weights: productWeights, product_threshold } = specialCondition;
        let weightConditions = productWeights;
        if (enableNonStandardWeights && productWeights?.includes('each')) {
          weightConditions = EXTRACT.map(({ id }) => id);
        }

        if (weightConditions?.length && !weightConditions.includes(weightId)) {
          return false;
        }

        if (product_threshold) {
          const { minimum_price, maximum_price } = product_threshold;
          if (minimum_price && price < minimum_price) return false;
          if (maximum_price && price > maximum_price) return false;
        }
      }

      return true;
    }

    // Handle bulk pricing conditions
    const cartWeight = qualifyingStatus?.product_weight ?? 0;
    const targetWeight = qualifyingStatus?.target_weight;
    if (
      targetWeight &&
      PRICE_ID_TO_GRAMS[weightId] > targetWeight - cartWeight
    ) {
      return false;
    }

    const bulkWeights =
      currentSpecial?.conditions?.bulk_pricing?.bulk_weights || [];
    if (!bulkWeights.length) return true;

    const weightCondition = bulkWeights.find((w) => w.price_id === weightId);
    if (!weightCondition) return false;

    const { minimum_price, maximum_price } = weightCondition;
    if (minimum_price && price < minimum_price) return false;
    if (maximum_price && price > maximum_price) return false;

    return true;
  };
};

// helper for shouldShowWeight. Checks if a weight is included in the special rules. includes price threshold checks.
function rulesIncludeWeight(
  includesWeights: string[],
  excludesWeights: string[],
  enableNonStandardWeights: boolean,
  productThreshold: ProductThresholdConditions | null | undefined,
  weightId: string,
  price: number
) {
  if (enableNonStandardWeights && includesWeights?.includes('each')) {
    includesWeights = EXTRACT.map(({ id }) => id);
  }
  if (excludesWeights.length) {
    includesWeights = includesWeights.filter(
      (weight) => !excludesWeights.includes(weight)
    );
  }

  if (includesWeights?.length && !includesWeights.includes(weightId)) {
    return false;
  }

  if (productThreshold) {
    const { minimum_price, maximum_price } = productThreshold;
    if (minimum_price && price < minimum_price) return false;
    if (maximum_price && price > maximum_price) return false;
  }

  return true;
}

/**
 * Filters out products that would have no valid weights after applying special conditions.
 * Products in cart are always kept regardless of weight restrictions.
 */
export const filterProductsByWeight = ({
  cartProducts = [],
  currentSpecial,
  products,
  qualifyingStatus,
  showOnlyWeights,
  enableNonStandardWeights,
}: ShouldShowWeight & {
  products:
    | SmartSortProduct<AlgoliaProduct>[]
    | VirtualBudtenderProduct<AlgoliaProduct>[];
}) => {
  if (!currentSpecial) return products;

  const weightFilter = shouldShowWeight({
    currentSpecial,
    cartProducts,
    qualifyingStatus,
    showOnlyWeights,
    enableNonStandardWeights,
  });

  return products.filter((product) => {
    const productKind = product.attributes['kind'];

    if (!isSoldByWeight(productKind)) return true;

    const productId = product.attributes['product_id'];

    const availableWeights = product.attributes['available_weights'];

    if (productInCart(cartProducts, productId)) return true;

    return (availableWeights || []).some((weight: string) => {
      const filter = weightFilter(weight, product.attributes);
      return filter;
    });
  });
};
